import { Form } from "react-final-form";
import { TextField } from "mui-rff";
import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Container, Typography } from "@mui/material";
import { useStartLogin } from "../../auth/useStartLogin";
import { useVerifyFactor } from "../../auth/useVerifyFactor";
import { useResendFactor } from "../../auth/useResendFactor";
import { Box } from "@mui/system";
import { TFactor, FactorEnum } from "../../models/Factors";
import { FactorForm } from "./components/FactorForm";

type FormValues = {
  identity?: string;
  code?: string;
};

enum StepModel {
  Identity = "identity",
  Factor = "factor",
}

type StepType = StepModel.Identity | StepModel.Factor;

export const SigninScreen = () => {
  const [step, setStep] = useState<StepType>(StepModel.Identity);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const sessionId = useRef("");
  const identity = useRef("");
  const [code, setCode] = useState<string>("");
  const [currentFactor, setCurrentFactor] = useState<TFactor>({} as TFactor);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [isReady, setIsReady] = useState<boolean>(true);
  const [
    startLoginResult,
    startLoginLoading,
    startLoginError,
    startLoginRequest,
  ] = useStartLogin();
  const [
    verifyFactorResult,
    verifyFactorLoading,
    verifyFactorError,
    verifyFactorRequest,
  ] = useVerifyFactor();
  const [
    resendFactorResult,
    resendFactorLoading,
    resendFactorError,
    resendFactorRequest,
  ] = useResendFactor();

  useEffect(() => {
    setIsLoading(
      startLoginLoading || verifyFactorLoading || resendFactorLoading
    );
  }, [startLoginLoading, verifyFactorLoading, resendFactorLoading]);

  useEffect(() => {
    if (startLoginResult) {
      setStep(StepModel.Factor);
      setCurrentFactor(startLoginResult.factor);
      sessionId.current = startLoginResult.authSessionId;
    }
  }, [startLoginResult]);

  useEffect(() => {
    if (verifyFactorResult?.nextFactor) {
      setCode("");
      setSuccessMessage("Verification successful");
      setCurrentFactor(verifyFactorResult.nextFactor);
    }
  }, [verifyFactorResult]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const onSubmit = useCallback(
    (formData: FormValues) => {
      if (step === StepModel.Identity && formData.identity) {
        startLoginRequest("LOGIN_FLOW_COCKPIT", formData.identity);
        identity.current = formData.identity;
      } else if (
        step === StepModel.Factor &&
        formData.identity &&
        sessionId.current &&
        currentFactor?.factorId
      ) {
        verifyFactorRequest(
          sessionId.current,
          formData.identity,
          currentFactor.factorId,
          code
        );
      } else {
        console.log("Invalid state");
      }
    },
    [
      currentFactor,
      startLoginRequest,
      step,
      sessionId,
      verifyFactorRequest,
      code,
    ]
  );

  const validate = useCallback(
    (formData: FormValues) => {
      const errors: { [key: string]: boolean } = {};

      if (step === StepModel.Identity && !formData.identity) {
        errors.identity = true;
      }

      if (step === StepModel.Factor) {
        const factorType = currentFactor?.factorType as FactorEnum;
        if (
          (factorType === FactorEnum.EmailCode ||
            factorType === FactorEnum.SmsCode) &&
          !formData.code
        ) {
          errors.code = true;
        }
      }

      return errors;
    },
    [step, currentFactor]
  );

  const resendCode = useCallback(async () => {
    if (sessionId && identity.current && currentFactor?.factorId) {
      try {
        await resendFactorRequest(
          sessionId.current,
          identity.current,
          currentFactor.factorId
        );
      } catch (e) {
        console.error(e);
      }
    }
  }, [sessionId, identity, currentFactor?.factorId, resendFactorRequest]);

  const error = startLoginError || verifyFactorError || resendFactorError;

  return (
    <Container maxWidth='sm'>
      <Box
        height='100vh'
        pb={5}
        pt={5}
        display='flex'
        flexDirection='column'
        justifyContent='center'
      >
        <Box minHeight={400}>
          <Typography variant='h4'>Sign in to VNX ID</Typography>
          <Box mb={2} />
          <Form
            onSubmit={onSubmit}
            validate={validate}
            render={({ handleSubmit }) => (
              <form onSubmit={handleSubmit} noValidate>
                {step === StepModel.Factor &&
                currentFactor.factorType === FactorEnum.SetPassword ? (
                  <Typography variant='body1' sx={{ mb: 2 }}>
                    Please set up a password to use it as the first factor to
                    access the cockpit
                  </Typography>
                ) : (
                  <TextField
                    disabled={step !== StepModel.Identity}
                    placeholder='+9... or email@example.com'
                    label='Phone Number or Email'
                    name='identity'
                    required={true}
                    size='small'
                    margin='normal'
                  />
                )}
                {step === StepModel.Factor ? (
                  <FactorForm
                    code={code}
                    setCode={setCode}
                    onResend={resendCode}
                    currentFactor={currentFactor}
                    onIsReadyChange={setIsReady}
                  />
                ) : null}
                <Button
                  sx={{ mt: 2 }}
                  type='submit'
                  fullWidth
                  variant='contained'
                  disabled={isLoading || !isReady}
                >
                  Continue
                </Button>
              </form>
            )}
          />

          {error !== null && (
            <Alert sx={{ mt: 2 }} severity='error'>
              {error.message}
            </Alert>
          )}

          {successMessage && (
            <Alert sx={{ mt: 2 }} severity='success'>
              {successMessage}
            </Alert>
          )}
        </Box>
      </Box>
    </Container>
  );
};
